import { api, ProjectDetail } from '../api';
import { escapeHtml, initials } from '../lib/utils';
import { toast } from '../components/toast';
import { openModal } from '../components/modal';
import { ProjectContext } from './project';

export async function renderMembers(
  content: HTMLElement,
  ctx: ProjectContext
): Promise<void> {
  const { project, tasks, reload, readOnly } = ctx;

  const pointsFor = (memberId: string) =>
    tasks
      .filter((t) => t.assigneeId === memberId && t.status === 'complete')
      .reduce((s, t) => s + t.storyPoints, 0);

  content.innerHTML = `
    <div class="member-layout">
      ${
        readOnly
          ? ''
          : `
        <div class="card">
          <h3 class="card-title">Add a member</h3>
          <form id="add-member-form">
            <div class="field">
              <label>Name</label>
              <input class="input" name="name" placeholder="e.g. Grace Hopper" />
            </div>
            <div class="field">
              <label>Email <span class="muted">(optional)</span></label>
              <input class="input" name="email" type="email" placeholder="member@example.com" />
            </div>
            <button class="btn btn-primary btn-block" type="submit">Add member</button>
          </form>
        </div>
      `
      }
      <div>
        ${
          project.members.length === 0
            ? `<div class="empty-state"><h3>No members yet</h3><p class="muted">${
                readOnly
                  ? 'This project has no members yet.'
                  : 'Add the people working on this project.'
              }</p></div>`
            : `
          <div class="member-list">
            ${project.members
              .map(
                (m) => `
              <div class="member-item ${readOnly ? '' : 'clickable'}" ${
                readOnly ? '' : `data-edit="${m.id}" title="Edit member"`
              }>
                <span class="name">
                  <span class="avatar">${initials(m.name)}</span>
                  <span class="member-name-text">
                    <span>${escapeHtml(m.name)}</span>
                    ${m.email ? `<span class="member-email">${escapeHtml(m.email)}</span>` : ''}
                  </span>
                </span>
                <span style="display:flex;align-items:center;gap:10px;">
                  <span class="points-chip">${pointsFor(m.id)} pts done</span>
                  ${
                    readOnly
                      ? ''
                      : `<button class="remove" data-remove="${m.id}" title="Remove member">×</button>`
                  }
                </span>
              </div>
            `
              )
              .join('')}
          </div>
        `
        }
      </div>
    </div>
  `;

  if (readOnly) return;

  content.querySelector('#add-member-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = content.querySelector<HTMLInputElement>('input[name="name"]')!;
    const emailInput = content.querySelector<HTMLInputElement>('input[name="email"]')!;
    const name = input.value.trim();
    const email = emailInput.value.trim();
    if (!name) return;
    try {
      await api.post(`/projects/${project.id}/members`, { name, email });
      input.value = '';
      emailInput.value = '';
      toast('Member added');
      reload();
    } catch (err: any) {
      toast(err.message || 'Could not add member', 'error');
    }
  });

  content.querySelectorAll<HTMLElement>('[data-edit]').forEach((item) => {
    item.addEventListener('click', () => {
      openEditMember(project, item.dataset.edit!, reload);
    });
  });

  content.querySelectorAll<HTMLElement>('[data-remove]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm('Remove this member? Their tasks will be unassigned.')) return;
      try {
        await api.del(`/projects/${project.id}/members/${btn.dataset.remove}`);
        toast('Member removed');
        reload();
      } catch (err: any) {
        toast(err.message || 'Could not remove member', 'error');
      }
    });
  });
}

function openEditMember(
  project: ProjectDetail,
  memberId: string,
  reload: () => void
): void {
  const member = project.members.find((m) => m.id === memberId);
  if (!member) return;

  openModal(
    `
    <h3 class="card-title">Edit member</h3>
    <form id="edit-member-form">
      <div class="field">
        <label>Name</label>
        <input class="input" name="name" value="${escapeHtml(member.name)}" placeholder="e.g. Grace Hopper" />
      </div>
      <div class="field">
        <label>Email <span class="muted">(optional)</span></label>
        <input class="input" name="email" type="email" value="${escapeHtml(member.email || '')}" placeholder="member@example.com" />
      </div>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Save changes</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#edit-member-form') as HTMLFormElement;
      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);
      body.querySelector<HTMLInputElement>('input[name="name"]')?.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        const email = String(data.get('email') || '').trim();
        if (!name) {
          toast('Member name is required', 'error');
          return;
        }
        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving…';
        try {
          await api.patch(`/projects/${project.id}/members/${memberId}`, { name, email });
          toast('Member updated');
          close();
          reload();
        } catch (err: any) {
          toast(err.message || 'Could not update member', 'error');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Save changes';
        }
      });
    }
  );
}
